import type { IconName } from './icons';

/**
 * Arbre des talents en **graphe** (refonte F1, lot 2) : disposition, géométrie,
 * forme et icône des nœuds. Module **pur** (aucun DOM, aucune donnée en dur) :
 * l'écran `screens/accalmie/tree.ts` le nourrit avec `src/data/nodes.ts` et
 * l'état de la partie ; `tests/ui/treeGraph.test.ts` le vérifie.
 *
 * ── Disposition ──────────────────────────────────────────────────────────
 * Une grille de **rangées** globales (la racine seule en rangée 0) et de
 * **couloirs** (un par branche, dans l'ordre `LANE_ORDER` puis d'apparition),
 * chaque couloir découpé en trois colonnes : gauche (¼), centre (½), droite (¾).
 * Règle d'occupation d'une rangée dans un couloir : **un nœud au centre seul**,
 * ou **jusqu'à deux nœuds** à gauche et à droite (l'étiquette d'un nœud central
 * occupe toute la largeur du couloir, celles des côtés une moitié chacune).
 *
 * Placement glouton en largeur depuis la racine, enfants dans l'ordre des
 * données. Colonne souhaitée : enfant unique → la colonne du parent (traits
 * droits) ; fratrie → gauche, droite, puis centre ; enfant de la racine seul
 * dans son couloir → centre. Pour chaque enfant on cherche, rangée après
 * rangée à partir de celle qui suit le parent, la première case libre (colonne
 * souhaitée d'abord, puis les côtés, puis le centre) dont le **chemin du lien**
 * est dégagé. Le lien descend du parent, longe une barre horizontale juste sous
 * lui, puis descend dans la colonne de l'enfant : les cases traversées par ce
 * dernier tronçon sont réservées (« passages ») et aucun nœud ne s'y pose
 * ensuite. Repli garanti (terminaison) : une rangée neuve sous tout le reste.
 *
 * v0.26.0 (53 nœuds) : **réservation** de la rangée qui suit un nœud ayant
 * des enfants dans son couloir — la case de sa colonne (enfant unique) ou les
 * deux côtés (fratrie). Tant que ses enfants ne sont pas placés, aucun autre
 * nœud ni lien ne prend ces cases : un nœud central posé plus tôt dans le
 * parcours en largeur ne peut plus murer la descente d'une chaîne latérale
 * (cas *Coup de fouet* → *Esclavagisme* sous *PentaDice*). Les réservations
 * restantes sont libérées dès que la fratrie est placée.
 *
 * Conséquences : la rangée d'un nœud est toujours ≥ sa profondeur et > celle de
 * son parent ; deux nœuds ne se chevauchent jamais ; un nœud ajouté dans
 * `nodes.ts` apparaît sans rien toucher ici.
 */

/** Entrée minimale d'un nœud (sous-ensemble de `NodeDef`). */
export interface GraphNodeInput {
  id: string;
  name: string;
  branch: string;
  parent: string | null;
  maxLevel: number;
  description?: string;
}

/** Colonne dans un couloir : 0 gauche, 1 centre, 2 droite. */
export type LaneCol = 0 | 1 | 2;
export const COL_LEFT: LaneCol = 0;
export const COL_CENTER: LaneCol = 1;
export const COL_RIGHT: LaneCol = 2;

export interface GraphCell {
  /** Index du couloir, `null` pour une racine (centrée sur tout le graphe). */
  lane: number | null;
  col: LaneCol;
  row: number;
}

export interface GraphLink {
  from: string;
  to: string;
}

export interface GraphLayout {
  /** Branches des couloirs, de gauche à droite. */
  lanes: string[];
  cells: Record<string, GraphCell>;
  /** Nombre de rangées (dernière rangée occupée + 1). */
  rows: number;
  links: GraphLink[];
  /** Ordre de placement (parents toujours avant leurs enfants). */
  order: string[];
}

/**
 * Ordre préféré des couloirs ; une branche inconnue s'ajoute à droite. L'Abîme
 * (A94, v0.46.0) est le quatrième : sa chaîne tient dans la colonne centrale.
 */
export const LANE_ORDER = ['arsenal', 'shop', 'arcane', 'abyss'];

/**
 * Largeur relative d'un couloir (1 par défaut). L'Abîme est une chaîne de dix
 * nœuds au centre, sans colonnes latérales : un couloir plus étroit laisse aux
 * trois autres, qui portent des fratries, presque leur largeur d'avant.
 */
export const LANE_WEIGHT: Readonly<Record<string, number>> = { abyss: 0.8 };

type Occupant =
  | { kind: 'node'; id: string }
  | { kind: 'pass'; parent: string }
  /** Case gardée pour les enfants de `parent` (voir l'en-tête du module). */
  | { kind: 'reserve'; parent: string };

class Grid {
  /** `lane:row:col` → occupant. */
  private cells = new Map<string, Occupant>();
  maxRow = 0;

  private key(lane: number, row: number, col: LaneCol): string {
    return `${lane}:${row}:${col}`;
  }

  get(lane: number, row: number, col: LaneCol): Occupant | undefined {
    return this.cells.get(this.key(lane, row, col));
  }

  set(lane: number, row: number, col: LaneCol, occ: Occupant): void {
    this.cells.set(this.key(lane, row, col), occ);
    if (row > this.maxRow) this.maxRow = row;
  }

  delete(lane: number, row: number, col: LaneCol): void {
    this.cells.delete(this.key(lane, row, col));
  }

  /** Libère les cases encore réservées pour les enfants de `parent`. */
  release(parent: string): void {
    for (const [key, occ] of this.cells) {
      if (occ.kind === 'reserve' && occ.parent === parent) this.cells.delete(key);
    }
  }

  /** Case libre pour un enfant de `parent` (vide, ou réservée pour lui). */
  private freeFor(lane: number, row: number, col: LaneCol, parent: string): boolean {
    const o = this.get(lane, row, col);
    return !o || (o.kind === 'reserve' && o.parent === parent);
  }

  /** Le centre de la rangée interdit-il un nœud ou un passage latéral ? */
  private centerBlocks(lane: number, row: number, parent: string): boolean {
    const c = this.get(lane, row, COL_CENTER);
    return !!c && (c.kind === 'node' || (c.kind === 'reserve' && c.parent !== parent));
  }

  /** Un enfant de `parent` peut-il se poser en (row, col) ? */
  canPlace(lane: number, row: number, col: LaneCol, parent: string): boolean {
    if (!this.freeFor(lane, row, col, parent)) return false;
    if (col === COL_CENTER) {
      // Le centre veut la rangée pour lui seul (nœuds et passages latéraux).
      return this.freeFor(lane, row, COL_LEFT, parent) && this.freeFor(lane, row, COL_RIGHT, parent);
    }
    // Un côté refuse un nœud central (son étiquette couvre le couloir).
    return !this.centerBlocks(lane, row, parent);
  }

  /** Un lien issu de `parent` peut-il traverser (row, col) ? */
  canPass(lane: number, row: number, col: LaneCol, parent: string): boolean {
    const here = this.get(lane, row, col);
    if (here && !((here.kind === 'pass' || here.kind === 'reserve') && here.parent === parent)) return false;
    if (col !== COL_CENTER) return !this.centerBlocks(lane, row, parent);
    return true; // un passage central glisse entre deux nœuds latéraux
  }
}

/** Couloirs : branches des nœuds non racines, ordre préféré puis d'apparition. */
export function laneOrder(nodes: readonly GraphNodeInput[], preferred: readonly string[] = LANE_ORDER): string[] {
  const present: string[] = [];
  for (const n of nodes) {
    if (n.parent === null) continue;
    if (!present.includes(n.branch)) present.push(n.branch);
  }
  const known = preferred.filter((b) => present.includes(b));
  return [...known, ...present.filter((b) => !known.includes(b))];
}

/** Profondeur (racine = 0) calculée par remontée des parents. */
export function depthOf(id: string, byId: ReadonlyMap<string, GraphNodeInput>): number {
  let depth = 0;
  let cur = byId.get(id);
  while (cur?.parent) {
    depth += 1;
    cur = byId.get(cur.parent);
    if (depth > byId.size) break;
  }
  return depth;
}

/** Calcule la disposition du graphe (voir l'en-tête du module). */
export function layoutTree(nodes: readonly GraphNodeInput[], preferred: readonly string[] = LANE_ORDER): GraphLayout {
  const lanes = laneOrder(nodes, preferred);
  const laneIndex = new Map(lanes.map((b, i) => [b, i]));
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const children = new Map<string, GraphNodeInput[]>();
  const roots: GraphNodeInput[] = [];
  for (const n of nodes) {
    if (n.parent === null || !byId.has(n.parent)) roots.push(n);
    else {
      const list = children.get(n.parent) ?? [];
      list.push(n);
      children.set(n.parent, list);
    }
  }

  const grid = new Grid();
  const cells: Record<string, GraphCell> = {};
  const links: GraphLink[] = [];
  const order: string[] = [];

  /** Réserve la rangée sous `id` pour ses enfants du même couloir. */
  const reserveFor = (id: string, cell: GraphCell): void => {
    if (cell.lane === null) return;
    const lane = cell.lane;
    const kids = (children.get(id) ?? []).filter((k) => laneIndex.get(k.branch) === lane);
    if (kids.length === 0) return;
    const cols: LaneCol[] = kids.length === 1 ? [cell.col] : [COL_LEFT, COL_RIGHT];
    for (const col of cols) {
      if (!grid.get(lane, cell.row + 1, col)) grid.set(lane, cell.row + 1, col, { kind: 'reserve', parent: id });
    }
  };

  for (const r of roots) {
    // Les racines se partagent la rangée 0 ; une racine portée par un couloir
    // connu s'y range, sinon elle est centrée sur tout le graphe.
    const lane = r.parent === null ? null : (laneIndex.get(r.branch) ?? null);
    cells[r.id] = { lane, col: COL_CENTER, row: 0 };
    order.push(r.id);
  }

  const queue = roots.map((r) => r.id);
  while (queue.length > 0) {
    const pid = queue.shift() as string;
    const parentCell = cells[pid];
    const kids = children.get(pid) ?? [];
    // Fratrie par couloir : la colonne souhaitée dépend du nombre de frères
    // qui partagent le couloir.
    const perLane = new Map<number, GraphNodeInput[]>();
    for (const k of kids) {
      const lane = laneIndex.get(k.branch);
      if (lane === undefined) continue;
      const list = perLane.get(lane) ?? [];
      list.push(k);
      perLane.set(lane, list);
    }
    for (const kid of kids) {
      const lane = laneIndex.get(kid.branch);
      if (lane === undefined) continue;
      const siblings = perLane.get(lane) ?? [kid];
      const rank = siblings.indexOf(kid);
      const sameLane = parentCell.lane === lane;
      let pref: LaneCol;
      if (siblings.length === 1) pref = sameLane ? parentCell.col : COL_CENTER;
      else pref = ([COL_LEFT, COL_RIGHT, COL_CENTER] as LaneCol[])[rank % 3];
      const tryOrder: LaneCol[] = [pref, ...([COL_LEFT, COL_RIGHT, COL_CENTER] as LaneCol[]).filter((c) => c !== pref)];

      let placed: GraphCell | null = null;
      const limit = parentCell.row + 1 + nodes.length * 2;
      for (let row = parentCell.row + 1; row <= limit && !placed; row++) {
        for (const col of tryOrder) {
          if (!grid.canPlace(lane, row, col, pid)) continue;
          let clear = true;
          for (let k = parentCell.row + 1; k < row && clear; k++) {
            if (!grid.canPass(lane, k, col, pid)) clear = false;
          }
          if (clear) {
            placed = { lane, col, row };
            break;
          }
        }
      }
      if (!placed) {
        // Repli : une rangée neuve, sous tout ce qui existe déjà.
        placed = { lane, col: pref, row: grid.maxRow + 1 };
      } else {
        for (let k = parentCell.row + 1; k < placed.row; k++) {
          grid.set(lane, k, placed.col, { kind: 'pass', parent: pid });
        }
      }
      grid.set(lane, placed.row, placed.col, { kind: 'node', id: kid.id });
      cells[kid.id] = placed;
      reserveFor(kid.id, placed);
      links.push({ from: pid, to: kid.id });
      order.push(kid.id);
      queue.push(kid.id);
    }
    grid.release(pid);
  }

  let rows = 1;
  for (const c of Object.values(cells)) rows = Math.max(rows, c.row + 1);
  return { lanes, cells, rows, links, order };
}

// ───────────────────────────────────────────── Nœuds dévoilés (A98)

/**
 * Branches que le dévoilement ne touche pas : l'Abîme garde ses règles (A94 —
 * ses nœuds existent tous dès l'Artisan des dés, et ce sont les portes de
 * niveau qui les ferment).
 */
export const REVEAL_EXEMPT_BRANCHES: readonly string[] = ['abyss'];

export interface RevealOptions {
  /** Tout montrer (case de debug « Tout l'arbre »). */
  revealAll?: boolean;
  /** Branches jamais masquées (défaut : {@link REVEAL_EXEMPT_BRANCHES}). */
  exemptBranches?: readonly string[];
}

/**
 * **Nœuds à dessiner** (v0.49.0, A98 — COMMENTS v0.48.1 §2) : au début de la
 * campagne seuls la racine et ses enfants directs (Petit lancer, Butin,
 * Journée longue) se voient ; acheter un nœud **dévoile ses enfants**. Un nœud
 * est visible quand
 *   - il est la racine, ou l'enfant d'une racine ;
 *   - ou son parent est possédé (niveau ≥ 1) ;
 *   - ou il est lui-même possédé (un achat de debug, une sauvegarde ancienne :
 *     un nœud acquis ne disparaît jamais) ;
 *   - ou sa branche est exemptée (l'Abîme) ;
 *   - ou `revealAll` est vrai (debug).
 * Un nœud dont le parent n'est pas dans la liste est traité comme une racine.
 * **Pure** : la disposition (`layoutTree`) reste calculée sur tous les nœuds,
 * c'est l'écran qui ne dessine que ceux-ci — rien ne bouge quand un nœud apparaît.
 */
export function visibleNodeIds(
  nodes: readonly GraphNodeInput[],
  levels: Readonly<Record<string, number>>,
  opts: RevealOptions = {},
): Set<string> {
  const out = new Set<string>();
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const exempt = opts.exemptBranches ?? REVEAL_EXEMPT_BRANCHES;
  const owned = (id: string): boolean => (levels[id] ?? 0) >= 1;
  for (const n of nodes) {
    const parent = n.parent === null ? null : byId.get(n.parent);
    const rootLike = n.parent === null || !parent;
    const rootChild = !!parent && parent.parent === null;
    if (
      opts.revealAll === true ||
      rootLike ||
      rootChild ||
      exempt.includes(n.branch) ||
      owned(n.id) ||
      (!!parent && owned(parent.id))
    ) {
      out.add(n.id);
    }
  }
  return out;
}

// ───────────────────────────────────────────── Géométrie (px)

/** Constantes de dessin (px CSS). */
export const GRAPH = {
  /** Centre de la rangée 0 (sous les titres de couloir). */
  top: 70,
  /** Pas vertical entre deux rangées : nœud, étiquette sur trois lignes, lien. */
  rowH: 104,
  /** Marge sous la dernière rangée (étiquette + respiration). */
  bottom: 84,
  /** Demi-hauteur visuelle d'un nœud (départ et arrivée des liens). */
  nodeHalf: 21,
  /** Barre horizontale des liens, sous le parent (fraction du pas). */
  bus: 0.76,
  /** Haut de l'étiquette sous le centre du nœud. */
  labelTop: 25,
} as const;

export interface NodeBox {
  x: number;
  y: number;
  /** Largeur de l'étiquette, centrée sur `x`. */
  labelW: number;
}

/** Emprise horizontale d'un couloir (px). */
export interface LaneBox {
  left: number;
  width: number;
}

export interface GraphGeometry {
  width: number;
  height: number;
  /** Largeur moyenne d'un couloir (`width / nombre de couloirs`). */
  laneW: number;
  /** Emprise de chaque couloir, de gauche à droite (les poids de `LANE_WEIGHT`). */
  lanes: LaneBox[];
  nodes: Record<string, NodeBox>;
  /** Tracé SVG de chaque lien, clé `from>to`. */
  paths: Record<string, string>;
}

/** Abscisse d'une colonne dans un couloir (¼, ½, ¾ de son emprise). */
function colX(box: LaneBox, col: LaneCol): number {
  return box.left + (box.width * (col + 1)) / 4;
}

/** Positions en px pour une largeur de graphe donnée. */
export function graphGeometry(layout: GraphLayout, width: number): GraphGeometry {
  const laneCount = Math.max(1, layout.lanes.length);
  const laneW = width / laneCount;
  // Couloirs pondérés (`LANE_WEIGHT`) : l'Abîme, étroit, rend de la place aux autres.
  const weights = layout.lanes.map((b) => LANE_WEIGHT[b] ?? 1);
  const totalWeight = weights.reduce((sum, w) => sum + w, 0) || 1;
  const lanes: LaneBox[] = [];
  let cursor = 0;
  for (const w of weights) {
    const lw = (width * w) / totalWeight;
    lanes.push({ left: cursor, width: lw });
    cursor += lw;
  }
  const nodes: Record<string, NodeBox> = {};
  for (const [id, cell] of Object.entries(layout.cells)) {
    const y = GRAPH.top + cell.row * GRAPH.rowH;
    if (cell.lane === null) {
      nodes[id] = { x: width / 2, y, labelW: Math.min(width - 16, Math.round(laneW * 1.3)) };
      continue;
    }
    const box = lanes[cell.lane] ?? { left: 0, width: laneW };
    const x = colX(box, cell.col);
    const labelW = cell.col === COL_CENTER ? box.width - 12 : box.width / 2 - 4;
    nodes[id] = { x, y, labelW: Math.floor(labelW) };
  }
  const paths: Record<string, string> = {};
  for (const link of layout.links) {
    const p = nodes[link.from];
    const c = nodes[link.to];
    if (!p || !c) continue;
    const y1 = p.y + GRAPH.nodeHalf;
    const y2 = c.y - GRAPH.nodeHalf - 1;
    const bus = p.y + GRAPH.rowH * GRAPH.bus;
    const r = (n: number): string => String(Math.round(n * 10) / 10);
    paths[`${link.from}>${link.to}`] =
      Math.abs(p.x - c.x) < 0.5
        ? `M${r(p.x)} ${r(y1)}V${r(y2)}`
        : `M${r(p.x)} ${r(y1)}V${r(bus)}H${r(c.x)}V${r(y2)}`;
  }
  const height = GRAPH.top + (layout.rows - 1) * GRAPH.rowH + GRAPH.bottom;
  return { width, height, laneW, lanes, nodes, paths };
}

// ───────────────────────────────────────────── Forme, état, icône

/** Forme d'un nœud : carré (racine), hexagone (déblocage), rond (à niveaux). */
export type NodeShape = 'square' | 'hex' | 'round';

export function nodeShape(node: Pick<GraphNodeInput, 'parent' | 'maxLevel'>): NodeShape {
  if (node.parent === null) return 'square';
  return node.maxLevel <= 1 ? 'hex' : 'round';
}

/**
 * État d'affichage : `owned` acquis (bordure d'or), `buy` achetable maintenant
 * (mousse), `poor` débloqué mais or insuffisant (gris), `locked` parent manquant
 * **ou porte de l'Abîme fermée** (raison `'gate'`, A94 : gris, plus sombre,
 * cadenas). Un nœud acquis reste `owned` même s'il a encore des niveaux à
 * acheter : le badge et l'étiquette disent la suite.
 */
export type NodeVisualState = 'owned' | 'buy' | 'poor' | 'locked';

export function nodeVisualState(
  level: number,
  availability: { ok: boolean; reason?: string },
): NodeVisualState {
  if (level > 0) return 'owned';
  if (availability.ok) return 'buy';
  if (availability.reason === 'locked' || availability.reason === 'gate') return 'locked';
  return 'poor';
}

/** Icône de branche, à défaut d'une icône par mot-clé. */
export const BRANCH_ICON: Record<string, IconName> = {
  root: 'machine',
  arsenal: 'die',
  shop: 'gold',
  arcane: 'spell',
  abyss: 'abyss',
};

/**
 * Icône par mot-clé, cherchée dans le **nom** puis dans l'**effet** du nœud
 * (minuscules, accents compris) ; première règle qui répond. Ordre voulu : la
 * machine et le contenant avant les mots plus vagues (« dé », « or »).
 */
export const ICON_KEYWORDS: ReadonlyArray<{ icon: IconName; words: readonly string[] }> = [
  // A94 : la Forge et ses nœuds (Artisan des dés, Marteau Trempé, Carnet de recettes).
  { icon: 'forge', words: ['forge', 'marteau', 'artisan', 'recettes'] },
  { icon: 'machine', words: ['gachadice', 'machine gachapon'] },
  { icon: 'cup', words: ['gobelet', 'contenant', 'bol', 'seau', 'brouette'] },
  { icon: 'shield', words: ['conservation', 'conserv'] },
  { icon: 'target', words: ['critique', 'précis', 'lunette'] },
  { icon: 'clock', words: ['temps de retour', 'durée', 'journée', 'seconde'] },
  { icon: 'die', words: ['emplacement de dé'] },
  { icon: 'spell', words: ['sort', 'sorcellerie'] },
  { icon: 'gold', words: [' or ', 'd’or', "d'or", 'butin', 'larcin', 'orsplosion', 'coût'] },
];

export function nodeIconName(node: Pick<GraphNodeInput, 'name' | 'branch' | 'description'>): IconName {
  const name = ` ${node.name.toLowerCase()} `;
  const effect = ` ${(node.description ?? '').toLowerCase()} `;
  for (const source of [name, effect]) {
    for (const rule of ICON_KEYWORDS) {
      if (rule.words.some((w) => source.includes(w))) return rule.icon;
    }
  }
  return BRANCH_ICON[node.branch] ?? 'node';
}
